/* RunIt wishlist — storage and pure helpers (no DOM).
 *
 * Split out from assets/runit-wishlist.js the same way runit-consent-core.js is
 * split from runit-consent.js, so the sanitising/merge rules can be reasoned
 * about on their own.
 *
 * A guest's list is entirely on their device. A signed-in shopper's list also
 * lives on their Shopify customer account (`custom.wishlist`), and the account
 * half of that — the per-customer cache, the merge and the write queue — is in
 * assets/runit-wishlist-account.js. Nothing in THIS file talks to a server: it
 * is the storage shape and the rules both halves share. The only network use in
 * the storefront wishlist is the refresh in runit-wishlist.js, which reads
 * Shopify's own /products/<handle>.js to keep prices and stock honest.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (root) {
    root.RunItWishlistCore = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var KEY = 'runit:wishlist:v1';
  var PROMPT_SEEN_KEY = 'runit:wishlist:prompt-seen';
  var MAX_ITEMS = 200;
  var DEFAULT_FORMAT = '{{amount}}';

  function str(value) {
    return value === null || value === undefined ? '' : String(value);
  }

  function num(value) {
    var n = parseFloat(value);
    return isFinite(n) ? n : 0;
  }

  function bool(value, fallback) {
    if (value === true || value === 'true') return true;
    if (value === false || value === 'false') return false;
    return fallback;
  }

  /* A product payload hands back the main image as a plain URL but a variant's
   * image as an object, so normalise both before storing. */
  function imageUrl(value) {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return str(value.src || value.url || '');
    return '';
  }

  /* A stored selection: an array of option values. Shopify caps a product at
   * three options of 100 values, but a handful either way is plenty here, and
   * anything that is not a string is dropped rather than trusted. */
  function sanitiseOptions(raw) {
    if (!Array.isArray(raw)) return [];
    var out = [];
    for (var i = 0; i < raw.length && i < 6; i++) {
      out.push(str(raw[i]).slice(0, 80));
    }
    return out;
  }

  /* One stored product. Anything without an id and a handle is unusable — we
   * need the handle to link and refresh, and the id to de-duplicate. */
  function sanitiseEntry(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var id = str(raw.id);
    var handle = str(raw.handle);
    if (!id || !handle) return null;
    return {
      id: id,
      handle: handle,
      title: str(raw.title),
      price: num(raw.price),
      compare: num(raw.compare),
      image: str(raw.image),
      variantId: str(raw.variantId),
      /* Whether the shopper actually picked this variant. A heart on a product
       * card can only know the product's default variant, so saving from a card
       * leaves this false and the wishlist asks for a size instead of adding a
       * variant nobody chose. Saving from the cart line (that IS the variant they
       * picked) or picking a size sets it. Entries saved before this existed have
       * no flag and default to false for the same reason — never guess a size. */
      variantChosen: bool(raw.variantChosen, false),
      /* The variant's own title ("XL" / "XL / Reflective") so the row can print
       * the saved size without waiting for a product request. */
      variantTitle: str(raw.variantTitle),
      /* One value per Shopify option, in the product's own option order ("M",
       * "Reflective"). Empty strings are options the shopper has not picked yet.
       * Stored so a row can show what it is holding before its product payload
       * (and the option list) has been fetched. */
      options: sanitiseOptions(raw.options),
      available: bool(raw.available, true),
      /* True when the product's variants are priced differently, so the row can
       * say "From £50" exactly like the product card does. */
      hasRange: bool(raw.hasRange, false),
      addedAt: num(raw.addedAt)
    };
  }

  function sanitise(list) {
    if (!Array.isArray(list)) return [];
    var out = [];
    var seen = {};
    for (var i = 0; i < list.length; i++) {
      var entry = sanitiseEntry(list[i]);
      if (!entry || seen[entry.id]) continue;
      seen[entry.id] = true;
      out.push(entry);
      if (out.length >= MAX_ITEMS) break;
    }
    return out;
  }

  function copy(entry) {
    return {
      id: entry.id,
      handle: entry.handle,
      title: entry.title,
      price: entry.price,
      compare: entry.compare,
      image: entry.image,
      variantId: entry.variantId,
      variantChosen: entry.variantChosen,
      variantTitle: entry.variantTitle,
      options: (entry.options || []).slice(),
      available: entry.available,
      hasRange: entry.hasRange,
      addedAt: entry.addedAt
    };
  }

  /* Fold Shopify's /products/<handle>.js payload back into a stored entry, so a
   * price change or a sell-out shows up without the shopper re-saving the item.
   *
   * The shopper's variant is never swapped for another one behind their back: if
   * it has sold out the entry goes unavailable (the row prints SOLD OUT and lets
   * them pick another size), and if it has disappeared from the catalogue the
   * entry stops claiming a variant at all.
   *
   * `entry.available` is about the *saved variant*. Whether the product as a
   * whole is buyable is `hasAvailable(product)`, which the row uses to decide if
   * offering another size is worth it. */
  function applyProductData(entry, product) {
    if (!entry || !product) return entry;
    var updated = copy(entry);
    var variants = Array.isArray(product.variants) ? product.variants : [];
    if (product.title) updated.title = str(product.title);
    var featured = imageUrl(product.featured_image);
    if (featured) updated.image = featured;

    var stored = null;
    var firstAvailable = null;
    var lowest = null;
    var highest = null;
    var i;
    for (i = 0; i < variants.length; i++) {
      var price = num(variants[i].price);
      if (variants[i].available) {
        if (lowest === null || price < lowest) lowest = price;
        if (highest === null || price > highest) highest = price;
        if (!firstAvailable) firstAvailable = variants[i];
      }
      if (!stored && str(variants[i].id) === updated.variantId) stored = variants[i];
    }

    if (stored) {
      updated.variantTitle = str(stored.title);
      updated.available = stored.available === true;
      /* Entries saved before the selection was stored keep their variant — fill
       * the blanks from it rather than dropping what the shopper chose. Only for
       * a variant they actually chose: a card save carries the *default* variant
       * and reading its values would be guessing a size. */
      var variantOptions = updated.variantChosen && Array.isArray(stored.options) ? stored.options : [];
      var merged = (updated.options || []).slice();
      for (var o = 0; o < variantOptions.length; o++) {
        if (!str(merged[o])) merged[o] = str(variantOptions[o]);
      }
      updated.options = merged;
      /* The row always shows the price of the size it is holding, sold out or not. */
      updated.price = num(stored.price) || updated.price;
      updated.hasRange = false;
      updated.compare = num(stored.compare_at_price);
      var variantImage = imageUrl(stored.featured_image);
      if (variantImage) updated.image = variantImage;
      return updated;
    }

    /* The saved variant is gone from the product — a deleted or replaced variant.
     * Whatever we knew about it is no longer true, so drop the claim and let the
     * shopper choose again (the product may well still be buyable). */
    updated.variantId = '';
    updated.variantChosen = false;
    updated.variantTitle = '';
    updated.options = [];
    updated.available = false;
    if (firstAvailable) {
      updated.price = lowest;
      updated.hasRange = highest > lowest;
      updated.image = imageUrl(firstAvailable.featured_image) || updated.image;
    } else {
      updated.price = num(product.price);
      updated.hasRange = false;
    }
    return updated;
  }

  /* The cheapest buyable variant, for a "From £X" label and for asking whether
   * another size is worth offering. */
  function availableVariants(product) {
    if (!product || !Array.isArray(product.variants)) return [];
    var out = [];
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) out.push(product.variants[i]);
    }
    return out;
  }

  function optionNamesOf(product) {
    return (product && product.options ? product.options : []).map(function (option) {
      return str(typeof option === 'object' ? option.name : option);
    });
  }

  function sizeOptionIndex(product) {
    var names = optionNamesOf(product);
    for (var n = 0; n < names.length; n++) {
      if (/size/i.test(names[n])) return n;
    }
    return -1;
  }

  /* ------------------------------------------------------------------ options
   *
   * Shopify models a product as N options, each with values, and a variant as
   * one value per option. The row must offer exactly the product's own options —
   * Size, Print Type, Colour, whatever it actually has — and must never assume
   * "Size" exists or that it is the only one. Nothing below hard-codes an option
   * name; `isSize` is only ever used to pick wording ("Choose a size").
   */

  /* [{ index, name, values: [...], isSize }] — values in the order Shopify
   * lists them (variant order), de-duplicated. Options with no values at all
   * are dropped. */
  function optionGroups(product) {
    var raw = product && Array.isArray(product.options) ? product.options : [];
    var groups = [];
    for (var n = 0; n < raw.length; n++) {
      var option = raw[n];
      var name = str(option && typeof option === 'object' ? option.name : option);
      if (!name) continue;
      /* /products/<handle>.js declares each option's values in Shopify's own
       * order, which is the order the shopper sees on the PDP — prefer it. */
      var declared = option && Array.isArray(option.values) ? option.values : [];
      var values = [];
      for (var d = 0; d < declared.length; d++) {
        if (str(declared[d]) && values.indexOf(str(declared[d])) === -1) values.push(str(declared[d]));
      }
      groups.push({ index: n, name: name, values: values, isSize: /size/i.test(name) });
    }
    if (!groups.length) return groups;
    /* Anything a variant carries that the declaration did not list still counts,
     * so a value is never hidden just because the payload was thin. */
    var variants = Array.isArray(product.variants) ? product.variants : [];
    for (var v = 0; v < variants.length; v++) {
      var values = Array.isArray(variants[v].options) ? variants[v].options : [];
      for (var g = 0; g < groups.length; g++) {
        var value = str(values[groups[g].index]);
        if (value && groups[g].values.indexOf(value) === -1) groups[g].values.push(value);
      }
    }
    return groups.filter(function (group) { return group.values.length > 0; });
  }

  /* Every option that has to be known to name a variant: single-value options
   * are filled in for the shopper (there is nothing to choose), values that are
   * not on this product are dropped, and blanks stay blank. */
  function normaliseSelection(groups, selection) {
    var wanted = Array.isArray(selection) ? selection.slice() : [];
    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];
      var value = str(wanted[group.index]);
      if (group.values.length === 1) {
        wanted[group.index] = group.values[0];
      } else if (value && group.values.indexOf(value) === -1) {
        wanted[group.index] = '';
      } else {
        wanted[group.index] = value;
      }
    }
    return wanted;
  }

  /* The variant whose option values match the selection. A blank slot matches
   * anything, so this doubles as "is this combination real?". A buyable match
   * wins over a sold-out one; when every match is sold out the sold-out variant
   * is returned so the row can say so instead of pretending nothing is chosen. */
  function variantForSelection(product, selection) {
    var variants = product && Array.isArray(product.variants) ? product.variants : [];
    var wanted = Array.isArray(selection) ? selection : [];
    var soldOut = null;
    for (var v = 0; v < variants.length; v++) {
      var values = Array.isArray(variants[v].options) ? variants[v].options : [];
      var ok = true;
      for (var i = 0; i < wanted.length; i++) {
        if (!str(wanted[i])) continue;
        if (str(values[i]) !== str(wanted[i])) { ok = false; break; }
      }
      if (!ok) continue;
      if (variants[v].available === true) return variants[v];
      if (!soldOut) soldOut = variants[v];
    }
    return soldOut;
  }

  /* One value of one option, seen from the rest of the selection: does a variant
   * exist for it, is it buyable, and which variant is it. This is what makes a
   * combination that is not offered (or is sold out) show as unavailable instead
   * of being submittable. */
  function valueState(product, index, value, selection) {
    var variants = product && Array.isArray(product.variants) ? product.variants : [];
    var wanted = Array.isArray(selection) ? selection : [];
    var out = {
      value: str(value),
      label: str(value),
      selected: str(wanted[index]) === str(value),
      exists: false,
      available: false,
      variantId: '',
      price: 0
    };
    for (var v = 0; v < variants.length; v++) {
      var values = Array.isArray(variants[v].options) ? variants[v].options : [];
      if (str(values[index]) !== str(value)) continue;
      var ok = true;
      for (var i = 0; i < wanted.length; i++) {
        if (i === index || !str(wanted[i])) continue;
        if (str(values[i]) !== str(wanted[i])) { ok = false; break; }
      }
      if (!ok) continue;
      out.exists = true;
      if (!out.variantId) {
        out.variantId = str(variants[v].id);
        out.price = num(variants[v].price);
      }
      if (variants[v].available === true) {
        out.available = true;
        out.variantId = str(variants[v].id);
        out.price = num(variants[v].price);
        break;
      }
    }
    out.disabled = !out.available;
    return out;
  }

  /* Everything the row needs to draw its pickers and name the variant:
   *
   *   groups      options with more than one value, each with per-value state
   *   autoSelected single-value options, filled in but not shown
   *   selection   the full selection, auto-selected values included
   *   variant     the exact variant the selection names, or null when the
   *               combination is not a real variant (every value that cannot
   *               complete the selection is disabled, so this is not reachable
   *               from the UI — but it is never treated as addable)
   *   missing     options the shopper has not picked yet
   *   complete    every option is named (the variant may still be missing)
   *   isSizeOnly  the only thing left to choose is a size (for copy) */
  function optionState(product, selection) {
    var groups = optionGroups(product);
    var wanted = normaliseSelection(groups, selection);
    var visible = [];
    var autoSelected = [];
    var missing = [];
    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];
      if (!str(wanted[group.index])) missing.push({ index: group.index, name: group.name, isSize: group.isSize });
      if (group.values.length === 1) {
        autoSelected.push({ index: group.index, name: group.name, value: group.values[0] });
        continue;
      }
      var values = [];
      for (var k = 0; k < group.values.length; k++) {
        values.push(valueState(product, group.index, group.values[k], wanted));
      }
      visible.push({ index: group.index, name: group.name, isSize: group.isSize, values: values });
    }
    var variant = groups.length ? variantForSelection(product, wanted) : null;
    return {
      groups: visible,
      autoSelected: autoSelected,
      selection: wanted,
      variant: variant,
      missing: missing,
      complete: groups.length > 0 && missing.length === 0,
      isSizeOnly: visible.length === 1 && visible[0].isSize,
      hasPrompt: visible.length > 0
    };
  }

  /* Wording for a button that cannot add yet: which option is outstanding. */
  function promptFor(state) {
    var missing = (state && state.missing) || [];
    if (!missing.length) return '';
    if (missing.length > 1) return 'CHOOSE OPTIONS';
    return 'CHOOSE ' + (missing[0].isSize ? 'A SIZE' : missing[0].name.toUpperCase());
  }

  /* The saved variant's title split for display: the size option value, any other
   * option values, and whether we actually found a size option to lead with.
   * Unsplit titles (sticker, single option) come back whole with isSize false. */
  function variantLabel(product, title) {
    var raw = str(title);
    var parts = raw.split(' / ');
    var sizeIndex = sizeOptionIndex(product);
    if (sizeIndex === -1 || !parts[sizeIndex]) return { size: raw, extra: '', isSize: false };
    return {
      size: str(parts[sizeIndex]),
      extra: parts.filter(function (_, i) { return i !== sizeIndex; }).join(' · '),
      isSize: true
    };
  }

  /* Subset of Shopify's money_format placeholders — the ones this store can
   * actually be configured with. Mirrors the Liquid output. */
  function formatMoney(cents, format) {
    var fmt = str(format) || DEFAULT_FORMAT;
    var noDecimals = fmt.indexOf('amount_no_decimals') !== -1;
    var commaSeparator = fmt.indexOf('comma_separator') !== -1;
    var apostrophe = fmt.indexOf('apostrophe_separator') !== -1;
    var value = num(cents) / 100;
    var text = value.toFixed(noDecimals ? 0 : 2);
    var parts = text.split('.');
    var thousands = commaSeparator ? '.' : apostrophe ? '\u2019' : ',';
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    var rendered = parts.join(commaSeparator ? ',' : '.');
    return fmt.replace(/\{\{\s*\w+\s*\}\}/g, rendered);
  }

  function readRaw(storage) {
    try {
      var raw = storage.getItem(KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function createStore(storage) {
    var listeners = [];

    function notify(items) {
      for (var i = 0; i < listeners.length; i++) {
        try {
          listeners[i](items);
        } catch (e) {}
      }
    }

    function write(items) {
      var clean = sanitise(items);
      try {
        storage.setItem(KEY, JSON.stringify(clean));
      } catch (e) {}
      notify(clean);
      return clean;
    }

    function all() {
      return sanitise(readRaw(storage));
    }

    function count() {
      return all().length;
    }

    function has(id) {
      var wanted = str(id);
      var items = all();
      for (var i = 0; i < items.length; i++) {
        if (items[i].id === wanted) return true;
      }
      return false;
    }

    function get(id) {
      var wanted = str(id);
      var items = all();
      for (var i = 0; i < items.length; i++) {
        if (items[i].id === wanted) return items[i];
      }
      return null;
    }

    function add(entry) {
      var clean = sanitiseEntry(entry);
      if (!clean) return all();
      if (!clean.addedAt) clean.addedAt = Date.now();
      var items = all();
      var next = [];
      var replaced = false;
      for (var i = 0; i < items.length; i++) {
        if (items[i].id === clean.id) {
          /* Re-saving refreshes the stored snapshot but keeps its position. */
          next.push(clean);
          replaced = true;
        } else {
          next.push(items[i]);
        }
      }
      if (!replaced) next.unshift(clean);
      return write(next);
    }

    function remove(id) {
      var wanted = str(id);
      var items = all();
      var next = [];
      for (var i = 0; i < items.length; i++) {
        if (items[i].id !== wanted) next.push(items[i]);
      }
      return write(next);
    }

    function toggle(entry) {
      if (has(entry && entry.id)) {
        remove(entry.id);
        return 'removed';
      }
      add(entry);
      return 'added';
    }

    function replace(items) {
      return write(items);
    }

    function clear() {
      return write([]);
    }

    function subscribe(fn) {
      listeners.push(fn);
      fn(all());
      return function unsubscribe() {
        var index = listeners.indexOf(fn);
        if (index !== -1) listeners.splice(index, 1);
      };
    }

    return {
      all: all,
      count: count,
      has: has,
      get: get,
      add: add,
      remove: remove,
      toggle: toggle,
      replace: replace,
      clear: clear,
      subscribe: subscribe
    };
  }

  /* --------------------------------------------------------------- account list
   *
   * A signed-in shopper's list lives in a Shopify customer metafield (see
   * assets/runit-wishlist-account.js for the network half), so it follows them to
   * every device. This section is the part worth testing on its own: the shape
   * that goes to the server, the merge rules, the per-customer cache keys and the
   * queue of writes the server has not acknowledged yet. No network, no DOM.
   *
   * What is stored server-side is the *minimum* that identifies a product: the
   * numeric product id, its handle, the variant the shopper actually chose (if
   * any) and when they saved it. Titles, prices, images and stock are deliberately
   * NOT stored, so a stale wishlist copy can never serve a wrong price — rows
   * hydrate from /products/<handle>.js exactly like the on-device list already
   * does, which is also why one item costs ~90 bytes on the server.
   */

  var CACHE_PREFIX = 'runit:wishlist:v1:acct:';
  var MERGED_PREFIX = 'runit:wishlist:merged:';
  var QUEUE_PREFIX = 'runit:wishlist:queue:';

  function cacheKey(customerId) {
    return CACHE_PREFIX + str(customerId);
  }

  function mergedKey(customerId) {
    return MERGED_PREFIX + str(customerId);
  }

  function queueKey(customerId) {
    return QUEUE_PREFIX + str(customerId);
  }

  /* Shopify hands out numeric ids from /products/<handle>.js but GIDs from the
   * Admin and Storefront APIs (`gid://shopify/Product/123`). Both mean the same
   * product, and a list written by one path must not look different to the other,
   * so everything is normalised to the numeric tail. */
  /* A product id from a GID, a bare numeric id, or a row that carries one — the
   * same unwrapping `accountItem` does, so the two cannot disagree about what an
   * id is. Returning `str()` of an object would hand back "[object Object]": a
   * truthy id no list contains, which is how a remove becomes a silent no-op. */
  function idFromGid(value) {
    var source = value && typeof value === 'object'
      ? (value.productId !== undefined ? value.productId : value.id)
      : value;
    var raw = str(source);
    var match = raw.match(/(\d+)\s*$/);
    return match ? match[1] : raw;
  }

  /* One server item, from either an existing entry or a payload the server sent
   * back. Unusable without a product id and a handle: the id de-duplicates and the
   * handle is what the row links to and hydrates from. */
  function accountItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var id = idFromGid(raw.productId !== undefined ? raw.productId : raw.id);
    var handle = str(raw.handle);
    if (!id || !handle) return null;
    return {
      productId: id,
      variantId: idFromGid(raw.variantId),
      handle: handle,
      addedAt: num(raw.addedAt)
    };
  }

  function sameItem(a, b) {
    if (!a || !b || a.productId !== b.productId) return false;
    return str(a.variantId) === str(b.variantId) && str(a.addedAt) === str(b.addedAt);
  }

  /* The account's DELETE record: `[{ productId, removedAt }]`, written by the
   * server whenever the shopper removes something. It is the only reason a stale
   * device cannot put an item back, so it is read before every merge: a local
   * entry a LATER removal has already undone is not an addition, it is a copy of
   * something the account has decided against. */
  function sanitiseTombstones(raw) {
    var list = Array.isArray(raw)
      ? raw
      : raw && typeof raw === 'object' && Array.isArray(raw.tombstones) ? raw.tombstones : [];
    var out = [];
    var newest = {};
    for (var i = 0; i < list.length; i++) {
      var entry = list[i];
      if (!entry || typeof entry !== 'object') continue;
      var id = idFromGid(entry.productId !== undefined ? entry.productId : entry.id);
      var at = num(entry.removedAt);
      if (!id || !at) continue;
      if (!newest[id] || at > newest[id].removedAt) {
        newest[id] = { productId: id, removedAt: at };
      }
    }
    for (var key in newest) {
      if (Object.prototype.hasOwnProperty.call(newest, key)) out.push(newest[key]);
    }
    return out;
  }

  /* When this product was last removed from the account, or 0 if it never was. */
  function removedAtFor(tombstones, id) {
    if (!tombstones || !tombstones.length) return 0;
    var wanted = idFromGid(id);
    for (var i = 0; i < tombstones.length; i++) {
      if (tombstones[i] && tombstones[i].productId === wanted) return num(tombstones[i].removedAt);
    }
    return 0;
  }

  /* Whether a local entry is a real addition or a copy of something removed.
   * The removal wins unless the local save is STRICTLY newer than it — ties go to
   * the removal, so the answer never depends on which device asked first. */
  function blockedByRemoval(tombstones, entry) {
    if (!entry) return false;
    var removedAt = removedAtFor(tombstones, entry.id);
    return removedAt > 0 && !(num(entry.addedAt) > removedAt);
  }

  function sanitiseAccountList(list) {
    if (!Array.isArray(list)) return [];
    var out = [];
    var seen = {};
    for (var i = 0; i < list.length; i++) {
      var item = accountItem(list[i]);
      if (!item || seen[item.productId]) continue;
      seen[item.productId] = true;
      out.push(item);
      if (out.length >= MAX_ITEMS) break;
    }
    return out;
  }

  /* A local entry for a server item we have never hydrated. The row draws what it
   * knows immediately (the link, and the saved size when there is one) and the
   * normal product refresh fills in title, price, image and the option pickers. */
  function entryFromAccountItem(item) {
    var clean = accountItem(item);
    if (!clean) return null;
    return {
      id: clean.productId,
      handle: clean.handle,
      title: '',
      price: 0,
      compare: 0,
      image: '',
      variantId: clean.variantId,
      variantChosen: !!clean.variantId,
      variantTitle: '',
      options: [],
      available: true,
      hasRange: false,
      addedAt: clean.addedAt
    };
  }

  /* The one stored entry for a product that exists on both sides. Neither side
   * wins wholesale: the local copy holds the hydrated display fields, while the
   * account copy is the shared truth.
   *
   * The saved variant is only ever taken from a side where the shopper actually
   * chose one (`variantChosen`) — a heart on a product card carries the product's
   * *default* variant, and reading a size out of that is the exact guessing this
   * file refuses to do everywhere else. When both sides chose one, the more
   * recently saved wins, so the tap that just happened is not undone by a sync of
   * an older list. */
  function combineEntries(localEntry, accountEntry) {
    var merged = copy(localEntry);
    var localChosen = !!localEntry.variantChosen && !!str(localEntry.variantId);
    var accountChosen = !!accountEntry.variantChosen && !!str(accountEntry.variantId);
    var source = localEntry;
    if (accountChosen && (!localChosen || num(accountEntry.addedAt) >= num(localEntry.addedAt))) {
      source = accountEntry;
    }
    if (source.variantId) {
      merged.variantId = source.variantId;
      merged.variantChosen = true;
      if (source.variantTitle) merged.variantTitle = source.variantTitle;
      if (source.options && source.options.length) merged.options = source.options.slice();
    }
    var before = num(localEntry.addedAt);
    var after = num(accountEntry.addedAt);
    /* The earlier save wins, so merging cannot keep re-positioning an item. */
    merged.addedAt = before && after ? Math.min(before, after) : (before || after);
    return merged;
  }

  /* Union of the on-device list and the account list, by product id, newest save
   * first. Idempotent: merging the same two lists twice produces the same list, so
   * a sync that runs on every page load cannot accumulate or reorder anything.
   *
   * Returns the merged entries plus the ids that the account did not already hold
   * (`added`), which is what the caller sends to the server. */
  function mergeLists(localEntries, accountItems, tombstones) {
    var local = sanitise(localEntries);
    var stones = sanitiseTombstones(tombstones);
    var byId = {};
    var i;
    for (i = 0; i < local.length; i++) byId[local[i].id] = local[i];

    var remote = sanitiseAccountList(accountItems);
    var out = [];
    var added = [];
    var seen = {};
    for (i = 0; i < remote.length; i++) {
      var item = remote[i];
      if (seen[item.productId]) continue;
      seen[item.productId] = true;
      var mine = byId[item.productId];
      if (!mine) {
        out.push(entryFromAccountItem(item));
        continue;
      }
      out.push(combineEntries(mine, entryFromAccountItem(item)));
    }
    for (i = 0; i < local.length; i++) {
      if (seen[local[i].id]) continue;
      seen[local[i].id] = true;
      /* Removed on another device AFTER this copy was saved: it is not an
       * addition to upload, and it is not drawn either. Keeping the local copy
       * here is precisely how an item comes back from the dead. */
      if (blockedByRemoval(stones, local[i])) continue;
      out.push(local[i]);
      added.push(local[i].id);
    }
    /* Newest first, like the store's own order. Entries with no recorded time keep
     * their relative order (Array#sort is stable), so this stays deterministic. */
    out.sort(function (a, b) { return num(b.addedAt) - num(a.addedAt); });
    return { items: out, added: added };
  }

  /* The cache's own shape: exactly the account's membership, with the display
   * fields of anything this device has already hydrated kept. A row the account no
   * longer has is NOT resurrected from the cache — the server decides membership,
   * the cache only remembers what a product looks like. */
  function cacheFromAccountItems(accountItems, knownEntries) {
    var known = sanitise(knownEntries);
    var byId = {};
    var i;
    for (i = 0; i < known.length; i++) byId[known[i].id] = known[i];
    var list = sanitiseAccountList(accountItems);
    var out = [];
    for (i = 0; i < list.length; i++) {
      var item = entryFromAccountItem(list[i]);
      var mine = byId[item.id];
      out.push(mine ? combineEntries(mine, item) : item);
    }
    out.sort(function (a, b) { return num(b.addedAt) - num(a.addedAt); });
    return out;
  }

  /* ------------------------------------------------------------- write queue
   *
   * The shopper's tap must never be lost to a failed request, so every change is
   * recorded locally first and sent after. Only the LAST operation per product
   * matters to the server — add then remove should end up removed, remove then add
   * should end up added with the newer variant — so the queue holds one entry per
   * product no matter how fast the heart is tapped, which also keeps it bounded. */
  var MERGE_SLOT = '*';

  function sanitiseQueue(raw) {
    if (!Array.isArray(raw)) return [];
    var out = [];
    var seen = {};
    for (var i = 0; i < raw.length; i++) {
      var op = raw[i];
      if (!op || typeof op !== 'object') continue;
      if (op.type === 'merge') {
        var batch = sanitiseAccountList(op.items);
        if (!batch.length || seen[MERGE_SLOT]) continue;
        seen[MERGE_SLOT] = true;
        out.push({ type: 'merge', productId: MERGE_SLOT, items: batch, at: num(op.at) });
        continue;
      }
      var type = op.type === 'add' ? 'add' : op.type === 'remove' ? 'remove' : '';
      var item = type === 'add' ? accountItem(op.item) : null;
      var id = item ? item.productId : idFromGid(op.productId);
      if (!type || !id || seen[id]) continue;
      seen[id] = true;
      out.push({ type: type, productId: id, item: item, at: num(op.at) });
    }
    return out;
  }

  function queueOperation(queue, op) {
    var clean = sanitiseQueue(queue);
    if (op && op.type === 'merge') {
      var batch = sanitiseAccountList(op.items);
      if (!batch.length) return clean;
      /* One merge at a time, and it goes first: it is the state the shopper
       * arrived with, and any single-product op queued after it is newer. */
      return [{ type: 'merge', productId: MERGE_SLOT, items: batch, at: num(op.at) }]
        .concat(clean.filter(function (entry) { return entry.type !== 'merge'; }));
    }
    var type = op && op.type === 'add' ? 'add' : op && op.type === 'remove' ? 'remove' : '';
    if (!type) return clean;
    var item = type === 'add' ? accountItem(op.item) : null;
    var id = item ? item.productId : idFromGid(op && op.productId);
    if (!id) return clean;
    var list = clean.filter(function (entry) {
      return entry.productId !== id;
    });
    list.push({ type: type, productId: id, item: item, at: num(op && op.at) });
    return list.slice(-MAX_ITEMS);
  }

  /* ---------------------------------------------------- storage for one list
   *
   * The UI keeps reading and writing a single list, so a signed-in shopper gets a
   * Storage-shaped view of their own per-customer cache and nothing else in
   * assets/runit-wishlist.js has to know which mode it is in. The cache is keyed
   * by customer id, so two accounts on one device can never see each other's
   * list, and the queue is keyed the same way. */
  function scopedStorage(storage, key) {
    return {
      getItem: function () {
        try {
          return storage.getItem(key);
        } catch (e) {
          return null;
        }
      },
      setItem: function (name, value) {
        try {
          storage.setItem(key, value);
        } catch (e) {}
      },
      removeItem: function () {
        try {
          storage.removeItem(key);
        } catch (e) {}
      }
    };
  }

  function readJson(storage, key, fallback) {
    try {
      var raw = storage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function writeJson(storage, key, value) {
    try {
      storage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      return false;
    }
  }

  function removeKey(storage, key) {
    try {
      storage.removeItem(key);
    } catch (e) {}
  }

  function readCache(storage, customerId) {
    return sanitise(readJson(storage, cacheKey(customerId), []));
  }

  function writeCache(storage, customerId, items) {
    return writeJson(storage, cacheKey(customerId), sanitise(items));
  }

  function readQueue(storage, customerId) {
    return sanitiseQueue(readJson(storage, queueKey(customerId), []));
  }

  function writeQueue(storage, customerId, queue) {
    var clean = sanitiseQueue(queue);
    if (!clean.length) removeKey(storage, queueKey(customerId));
    else writeJson(storage, queueKey(customerId), clean);
    return clean;
  }

  function readMerged(storage, customerId) {
    return readJson(storage, mergedKey(customerId), '') === '1';
  }

  function writeMerged(storage, customerId, value) {
    if (value) writeJson(storage, mergedKey(customerId), '1');
    else removeKey(storage, mergedKey(customerId));
  }

  return {
    KEY: KEY,
    PROMPT_SEEN_KEY: PROMPT_SEEN_KEY,
    MAX_ITEMS: MAX_ITEMS,
    CACHE_PREFIX: CACHE_PREFIX,
    cacheKey: cacheKey,
    mergedKey: mergedKey,
    queueKey: queueKey,
    idFromGid: idFromGid,
    accountItem: accountItem,
    sameItem: sameItem,
    sanitiseAccountList: sanitiseAccountList,
    sanitiseTombstones: sanitiseTombstones,
    removedAtFor: removedAtFor,
    blockedByRemoval: blockedByRemoval,
    entryFromAccountItem: entryFromAccountItem,
    mergeLists: mergeLists,
    cacheFromAccountItems: cacheFromAccountItems,
    sanitiseQueue: sanitiseQueue,
    queueOperation: queueOperation,
    scopedStorage: scopedStorage,
    readJson: readJson,
    writeJson: writeJson,
    removeKey: removeKey,
    readCache: readCache,
    writeCache: writeCache,
    readQueue: readQueue,
    writeQueue: writeQueue,
    readMerged: readMerged,
    writeMerged: writeMerged,
    sanitise: sanitise,
    sanitiseEntry: sanitiseEntry,
    applyProductData: applyProductData,
    availableVariants: availableVariants,
    optionGroups: optionGroups,
    optionState: optionState,
    variantForSelection: variantForSelection,
    promptFor: promptFor,
    variantLabel: variantLabel,
    sizeOptionIndex: sizeOptionIndex,
    formatMoney: formatMoney,
    createStore: createStore
  };
});
